/**
 * GanttFilters Component - Filters panel for Gantt chart
 */
import React from 'react';
import { Trash2 } from 'lucide-react';
import { MultiSelectFilter, FilterOption } from '../MultiSelectFilter';
import { STATUS_FILTER_OPTIONS } from '../../constants/status';

interface GanttFiltersProps {
  hospitalOptions: FilterOption[];
  clienteOptions: FilterOption[];
  auditorOptions: FilterOption[];
  codHospitalClienteOptions: FilterOption[];
  selectedHospitals: (string | number)[];
  selectedClientes: (string | number)[];
  selectedAuditores: (string | number)[];
  selectedStatus: (string | number)[];
  selectedCodHospitalCliente: (string | number)[];
  setSelectedHospitals: (values: (string | number)[]) => void;
  setSelectedClientes: (values: (string | number)[]) => void;
  setSelectedAuditores: (values: (string | number)[]) => void;
  setSelectedStatus: (values: (string | number)[]) => void;
  setSelectedCodHospitalCliente: (values: (string | number)[]) => void;
  allFiltersExpanded: boolean;
  setAllFiltersExpanded: (expanded: boolean) => void;
  onClearFilters: () => void;
}

export const GanttFilters: React.FC<GanttFiltersProps> = ({
  hospitalOptions,
  clienteOptions,
  auditorOptions,
  codHospitalClienteOptions,
  selectedHospitals,
  selectedClientes,
  selectedAuditores,
  selectedStatus,
  selectedCodHospitalCliente,
  setSelectedHospitals,
  setSelectedClientes,
  setSelectedAuditores,
  setSelectedStatus,
  setSelectedCodHospitalCliente,
  allFiltersExpanded,
  setAllFiltersExpanded,
  onClearFilters,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        {/* Grid de Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 flex-1">
          <MultiSelectFilter
            label="Clientes"
            options={clienteOptions}
            selectedValues={selectedClientes}
            onChange={setSelectedClientes}
            placeholder="Todos os clientes"
            expanded={allFiltersExpanded}
            showTodos={true}
            showBadges={true}
          />
          <MultiSelectFilter
            label="Hospital"
            options={hospitalOptions}
            selectedValues={selectedHospitals}
            onChange={setSelectedHospitals}
            placeholder="Todos os hospitais"
            expanded={allFiltersExpanded}
            showTodos={true}
            showBadges={true}
          />
          <MultiSelectFilter
            label="Cod. Hospital Cliente"
            options={codHospitalClienteOptions}
            selectedValues={selectedCodHospitalCliente}
            onChange={setSelectedCodHospitalCliente}
            placeholder="Todos os códigos"
            expanded={allFiltersExpanded}
            showTodos={true}
            showBadges={true}
          />
          <MultiSelectFilter
            label="Auditor/Operador"
            options={auditorOptions}
            selectedValues={selectedAuditores}
            onChange={setSelectedAuditores}
            placeholder="Todos os auditores"
            expanded={allFiltersExpanded}
            showTodos={true}
            showBadges={true}
          />
          <MultiSelectFilter
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            selectedValues={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="Todos os status"
            expanded={allFiltersExpanded}
            showTodos={true}
            showBadges={true}
          />
        </div>

        {/* Ações (Alinhadas à direita na mesma linha em telas grandes) */}
        <div className="flex items-center gap-3 pb-0.5 shrink-0">
          <button
            onClick={() => setAllFiltersExpanded(!allFiltersExpanded)}
            className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-3 py-2 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
          >
            {allFiltersExpanded ? (
              <>
                <span>▼</span>
                <span className="whitespace-nowrap">Recolher</span>
              </>
            ) : (
              <>
                <span>▶</span>
                <span className="whitespace-nowrap">Expandir</span>
              </>
            )}
          </button>

          <button
            onClick={onClearFilters}
            className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-2 rounded-lg transition-colors border border-transparent hover:border-red-100 dark:hover:border-red-800"
            title="Limpar todos os filtros"
          >
            <Trash2 className="w-4 h-4" />
            <span className="whitespace-nowrap">Limpar</span>
          </button>
        </div>
      </div>
    </div>
  );
};
